import {createAction, createAsyncThunk} from '@reduxjs/toolkit';

import getEventsDayOverview from 'api/events/get-events-day-overview';

import type {
    EventAttributionListItem,
    EventDayOverview,
} from 'api/events/events.types';
import type {DayOverviewState} from 'pages/event-management/store/day-overview-slice';
import dayjs from 'utils/dayjs';

const CANCELED = 'ERR_CANCELED';

export interface GetDayOverviewDataParams {
    facilityIds: number[];
    date: string;
    handleAbortSignal?: (signal: AbortSignal) => void;
    abortSignal?: AbortSignal;
}

interface GetDayOverviewDataThunkAPI {
    state: {
        events: {
            dayOverview: DayOverviewState;
        };
    };
    rejectValue: unknown;
}

/**
 * Formating function to sort events by their start date in ascending order.
 */
const sortEventsByStartDate = (
    events: EventAttributionListItem[]
): EventAttributionListItem[] => {
    const sortedEvents = [...events].sort((a, b) => {
        const startDateA = dayjs(a.eventStarts);
        const startDateB = dayjs(b.eventStarts);

        return startDateA.valueOf() - startDateB.valueOf();
    });

    return sortedEvents;
};

export const setDayOverviewData = createAction<EventDayOverview>(
    'events/day-overview/setData'
);
export const finishDayOverviewRequest = createAction<{error: boolean}>(
    'events/day-overview/finishRequest'
);

/**
 * A Redux thunk to get the events day overview data.
 *
 * Action Type: events/day-overview/getEventsDayOverviewData
 *
 * API Service: {@link getEventsDayOverview}
 */
export const getEventsDayOverviewData = createAsyncThunk<
    // Return type of the payload creator
    EventDayOverview,
    // First argument to the payload creator
    GetDayOverviewDataParams,
    // ThunkAPI type
    GetDayOverviewDataThunkAPI
>(
    'events/day-overview/getEventsDayOverviewData',
    async (
        {facilityIds, date, handleAbortSignal, abortSignal},
        {rejectWithValue, signal, dispatch, getState, requestId}
    ) => {
        const isCurrentRequest = () =>
            getState().events.dayOverview.currentRequestId === requestId;
        const finishRequest = (error: boolean) => {
            if (isCurrentRequest()) {
                dispatch(finishDayOverviewRequest({error}));
            }
        };
        const onAbort = () => finishRequest(false);

        signal.addEventListener('abort', onAbort);
        abortSignal?.addEventListener('abort', onAbort);

        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const formatedDate = dayjs(date).format('YYYY-MM-DD');

            const response = await getEventsDayOverview(
                facilityIds,
                formatedDate,
                {
                    signal: abortSignal || signal,
                }
            );

            const sortedEvents = sortEventsByStartDate(response.data.events);

            const formattedResponse = {
                counts: response.data.counts,
                events: sortedEvents,
            };

            if (
                isCurrentRequest() &&
                !signal.aborted &&
                !abortSignal?.aborted
            ) {
                dispatch(setDayOverviewData(formattedResponse));
                finishRequest(false);
            }

            return formattedResponse;
        } catch (error) {
            // If the error is due to the request being canceled, we don't want to log it
            if (
                error.code === CANCELED ||
                signal.aborted ||
                abortSignal?.aborted
            ) {
                finishRequest(false);
                const abortError = new Error('Aborted');

                abortError.name = 'AbortError';
                throw abortError;
            }

            finishRequest(true);
            console.error('getEventsDayOverviewData error:', {error});

            return rejectWithValue(error);
        } finally {
            signal.removeEventListener('abort', onAbort);
            abortSignal?.removeEventListener('abort', onAbort);
        }
    }
);

/**
 * An action to update the selected date for the day overview page.
 */
export const setSelectedDate = createAction(
    'events/day-overview/setSelectedDate',
    (date: string) => {
        return {
            payload: date,
        };
    }
);
